import * as React from 'react';
import classNames from 'classnames';
import Spoiler from '~/components/Spoiler/Spoiler';
import styles from '../common.scss';
import { t } from '~/utils/localization';
import WoWSEntity from '@wg/wows-entities/wrappers/react/WoWSEntity';
import { items as ITEM_TYPES, WarmingUpCollections, items } from '@wg/wows-entities/const/';
import { ManagerData } from '~/core/ManagerData';
import { CommanderPerks } from '~/components/Commanders/Commanders';
import CrewVoiceLabel from '~/components/CrewVoiceLabel/CrewVoiceLabel';
import { isInGameBrowser } from '~/utils/utils';
import CrewVoiceIconButton from '~/components/CrewVoiceIconButton/CrewVoiceIconButton';
import { DivTooltip } from '@wg/wows-react-uikit';
import DefaultTooltip from '~/components/Tooltip/DefaultTooltip';
import GuideDecorator from '~/decorators/GuideDecorator/GuideDecorator';
import { GUIDE_NAMES } from '~/components/WelcomePage/steps';

interface ICrewsSubTitleContentProps {
    item: IItemCommonData;
    renderPreviewContent?: boolean;
}

export const CrewsSubTitleContent = ({ item, renderPreviewContent }: ICrewsSubTitleContentProps) => {
    if (!item?.identifier) {
        return;
    }

    const crewData = ManagerData.getData(WarmingUpCollections.crews, item?.identifier, true);

    return (
        <>
            <div className={styles.preBundleTitle}>
                <CommanderPerks item={item} />
            </div>
            {crewData?.hasSampleVo && <CrewVoiceLabel className={styles.preBundleTitle} />}
            {isInGameBrowser && renderPreviewContent && crewData?.hasSampleVo && (
                <div className={styles.crewPreviewButton}>
                    <CrewVoiceIconButton crewId={item.identifier} />
                </div>
            )}
        </>
    );
};

interface ICrewBeforeDescriptionContent {
    bundle: IBundle;
}

export const CrewBeforeDescriptionContent = ({ bundle }: ICrewBeforeDescriptionContent) => {
    const primaryItem = bundle.primaryItem;
    if (!primaryItem?.identifier) {
        return;
    }

    const classes = classNames(styles.crewVehicleInfo, {
        [styles.borderBottom]: !bundle.description,
        [styles.withoutDescription]: !bundle.description,
    });

    const crewData = ManagerData.getData(WarmingUpCollections.crews, primaryItem.identifier, true);
    if (crewData.nation?.name === 'common' && !primaryItem.customisation?.shipId) {
        return (
            <div className={classes}>
                <GuideDecorator names={[GUIDE_NAMES.guide_nationless_crew]}>
                    <DivTooltip
                        className={styles.nationlessCrew}
                        tooltipBody={
                            <DefaultTooltip
                                text={t(
                                    "This commander currently has no nation assigned. Once you assign them to a ship, they will adopt that ship's nation. After assignment, the nation cannot be changed.",
                                )}
                            ></DefaultTooltip>
                        }
                    >
                        <span className="we-crew__nationless-icon"></span>
                        {t('No nation assigned')}
                    </DivTooltip>
                </GuideDecorator>
            </div>
        );
    }

    return (
        <div className={classes}>
            <div>{t('Trained for:')}</div>
            <div className={styles.crewVehicle}>
                <WoWSEntity
                    type={items.VEHICLES}
                    id={primaryItem.customisation?.shipId}
                    presentation={{
                        withTooltip: true,
                    }}
                />
            </div>
        </div>
    );
};

interface ICrewContent {
    item: IItemCommonData;
    renderSpoiler?: boolean;
}

const getCrewsContent = (props: ICrewContent) => {
    const crew = ManagerData.getCrew(props.item.identifier) || {};
    const isNeedToRender = !crew.talents?.length && !crew.skills?.length && !crew?.unlocks?.length;
    if (isNeedToRender) {
        return null;
    }

    const classesTitle = classNames({
        [styles.spoilerSubTitle]: props.renderSpoiler,
        [styles.contentBlockTitle]: !props.renderSpoiler,
    });

    const content = (
        <React.Fragment>
            {!!crew.talents?.length && (
                <div
                    className={classNames(styles.contentBlock, styles.borderBottom, {
                        [styles.spoilerContent]: props.renderSpoiler,
                    })}
                >
                    <div className={classesTitle}>{t('Таланты: ')}</div>
                    <div className={styles.contentBlockWowSEntities}>
                        <WoWSEntity
                            type={ITEM_TYPES.CREWS_TALENTS}
                            id={props.item.identifier}
                            presentation={{
                                withTooltip: true,
                                renderWithoutFade: true,
                            }}
                        />
                    </div>
                </div>
            )}
            {!!crew.skills?.length && (
                <div
                    className={classNames(styles.contentBlock, styles.borderBottom, {
                        [styles.spoilerContent]: props.renderSpoiler,
                    })}
                >
                    <div className={classesTitle}>{t('Улучшенные навыки: ')}</div>
                    <div className={styles.contentBlockWowSEntities}>
                        <WoWSEntity
                            type={ITEM_TYPES.CREWS_SKILLS}
                            id={props.item.identifier}
                            presentation={{
                                withTooltip: true,
                                renderWithoutFade: true,
                            }}
                        />
                    </div>
                </div>
            )}
            {!!crew?.unlocks?.length && (
                <div
                    className={classNames(styles.contentBlock, styles.borderBottom, {
                        [styles.spoilerContent]: props.renderSpoiler,
                    })}
                >
                    <div className={classesTitle}>{t('Персонализация: ')}</div>
                    <div className={styles.contentBlockWowSEntities}>
                        <WoWSEntity
                            type={ITEM_TYPES.CREWS_UNLOCKS}
                            id={props.item.identifier}
                            presentation={{
                                withTooltip: true,
                                renderWithoutFade: true,
                            }}
                        />
                    </div>
                </div>
            )}
        </React.Fragment>
    );

    if (props.renderSpoiler) {
        return (
            <Spoiler
                title={
                    <div className={classNames(styles.contentAdditionalBlock)}>
                        <div className={styles.additionalContentTitle}>
                            <WoWSEntity
                                key={props.item.identifier}
                                type={props.item.type}
                                id={props.item.identifier}
                                className={styles.weCrewComponent}
                                presentation={{
                                    withText: true,
                                    withoutAssetIcon: true,
                                }}
                            />
                        </div>
                    </div>
                }
                content={content}
            />
        );
    }

    return content;
};

export default getCrewsContent;
